import { ConstructorEnum, FilterRootGridType } from '@/interfaces/constructor.interfaces';
import { IConfigService } from '@/services/ConfigService';
import { II18Service } from '@/services/I18Service';
import InjectContainer from '@/services/InjectContainer';
import ServiceTypes from '@/services/ServiceTypes';
import mergeGrid from '@/utils/mergeGrid';

export const DEFAULT_FILTERS_IDS_MAP = {
  FILTERS: 'filters',
  FILTERS_LOCATION_GROUP: 'filters-location-group',
  FILTERS_CITY: 'filters-city',
  FILTERS_DEALERSHIP: 'filters-dealership',
  FILTERS_MODEL_GROUP: 'filters-model-group',
  FILTERS_MODEL: 'filters-model',
  FILTERS_PRICE_GROUP: 'filters-price-group',
  FILTERS_PRICE: 'filters-price',
  FILTERS_VERSION_GROUP: 'filters-version-group',
  FILTERS_VERSION: 'filters-version',
  FILTERS_ENGINE_GROUP: 'filters-engine-group',
  FILTERS_ENGINE: 'filters-engine',
  FILTERS_GEARBOX_GROUP: 'filters-gearbox-group',
  FILTERS_GEARBOX: 'filters-gearbox',
  FILTERS_DRIVETRAIN_GROUP: 'filters-drivetrain-group',
  FILTERS_DRIVETRAIN: 'filters-drivetrain',
  FILTERS_EXTERIOR_GROUP: 'filters-exterior-group',
  FILTERS_EXTERIOR: 'filters-exterior',
};

export const DEFAULT_FILTERS_KEYS_MAP = {
  CITY: 'city',
  DEALERSHIP: 'dealership',
  MODEL: 'model',
  PRICE: 'price',
  VERSION: 'version',
  ENGINE: 'engine',
  GEARBOX: 'gearbox',
  DRIVETRAIN: 'drivetrain',
  EXTERIOR: 'exterior',
};

export const GET_DEFAULT_FILTERS: () => FilterRootGridType = () => {
  const { i18n } = InjectContainer.get<II18Service>(ServiceTypes.I18N);
  const { preloadedState } = InjectContainer.get<IConfigService>(ServiceTypes.Config);

  const isBenefitsEnabled = preloadedState.config?.benefits?.enable || false;

  return {
    id: DEFAULT_FILTERS_IDS_MAP.FILTERS,
    type: ConstructorEnum.Filters,
    span: 24,
    items: [
      {
        id: DEFAULT_FILTERS_IDS_MAP.FILTERS_LOCATION_GROUP,
        title: i18n.t('defaultFilters.location.title'),
        gutter: [10, 10],
        order: 5,
        items: [
          {
            id: DEFAULT_FILTERS_IDS_MAP.FILTERS_CITY,
            key: DEFAULT_FILTERS_KEYS_MAP.CITY,
            path: 'locations.dealers_cities',
            span: 24,
            order: 1,
            type: ConstructorEnum.FilterLocation,
            hideSingleOption: true,
            props: {
              hideDisabledOptions: true,
            },
          },
          {
            id: DEFAULT_FILTERS_IDS_MAP.FILTERS_DEALERSHIP,
            key: DEFAULT_FILTERS_KEYS_MAP.DEALERSHIP,
            path: 'locations.dealers_dealerships',
            span: 24,
            order: 2,
            type: ConstructorEnum.FilterMultipleSelect,
            hideSingleOption: true,
            resetOnChange: [DEFAULT_FILTERS_KEYS_MAP.CITY],
            props: {
              label: i18n.t('defaultFilters.location.dealership.label'),
              showSearch: true,
              hideDisabledOptions: true,
              maxTagCount: 1000,
              maxTagTextLength: 12,
              filterOption: (input: string, option: any) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
              filterSort: (a: any, b: any) =>
                // eslint-disable-next-line no-unsafe-optional-chaining
                a?.disabled - b?.disabled ||
                (a?.label ?? '')
                  .toLowerCase()
                  .localeCompare((b?.label ?? '').toLowerCase()),
            },
          },
        ],
      },
      {
        id: DEFAULT_FILTERS_IDS_MAP.FILTERS_MODEL_GROUP,
        title: i18n.t('defaultFilters.models.title'),
        gutter: [20, 20],
        order: 10,
        items: [
          {
            id: DEFAULT_FILTERS_IDS_MAP.FILTERS_MODEL,
            key: DEFAULT_FILTERS_KEYS_MAP.MODEL,
            path: 'model',
            span: 24,
            order: 1,
            type: ConstructorEnum.FilterCheckboxGroup,
            props: {},
          },
        ],
      },
      {
        id: DEFAULT_FILTERS_IDS_MAP.FILTERS_PRICE_GROUP,
        title: i18n.t('defaultFilters.prices.title'),
        gutter: [20, 20],
        order: 15,
        items: [
          {
            id: DEFAULT_FILTERS_IDS_MAP.FILTERS_PRICE,
            key: DEFAULT_FILTERS_KEYS_MAP.PRICE,
            path: isBenefitsEnabled ? 'minPrice' : 'price',
            span: 24,
            order: 1,
            type: ConstructorEnum.FilterRangeSlider,
            props: {},
          },
        ],
      },
      {
        id: DEFAULT_FILTERS_IDS_MAP.FILTERS_VERSION_GROUP,
        title: i18n.t('defaultFilters.versions.title'),
        gutter: [20, 20],
        order: 20,
        items: [
          {
            id: DEFAULT_FILTERS_IDS_MAP.FILTERS_VERSION,
            key: DEFAULT_FILTERS_KEYS_MAP.VERSION,
            path: 'version',
            dependencies: [DEFAULT_FILTERS_KEYS_MAP.MODEL],
            dependencyTextFormatter: () =>
              i18n.t('defaultFilters.versions.dependencyText'),
            groupByKey: DEFAULT_FILTERS_KEYS_MAP.MODEL,
            span: 24,
            order: 1,
            type: ConstructorEnum.FilterCheckboxGroup,
            props: {},
          },
        ],
      },
      {
        id: DEFAULT_FILTERS_IDS_MAP.FILTERS_ENGINE_GROUP,
        title: i18n.t('defaultFilters.engines.title'),
        gutter: [20, 20],
        order: 25,
        items: [
          {
            id: DEFAULT_FILTERS_IDS_MAP.FILTERS_ENGINE,
            key: DEFAULT_FILTERS_KEYS_MAP.ENGINE,
            path: 'engine',
            span: 24,
            order: 1,
            type: ConstructorEnum.FilterCheckboxGroup,
            props: {},
          },
        ],
      },
      {
        id: DEFAULT_FILTERS_IDS_MAP.FILTERS_GEARBOX_GROUP,
        title: i18n.t('defaultFilters.gearboxes.title'),
        gutter: [20, 20],
        order: 30,
        items: [
          {
            id: DEFAULT_FILTERS_IDS_MAP.FILTERS_GEARBOX,
            key: DEFAULT_FILTERS_KEYS_MAP.GEARBOX,
            path: 'gearbox',
            span: 24,
            order: 1,
            type: ConstructorEnum.FilterCheckboxGroup,
            props: {},
          },
        ],
      },
      {
        id: DEFAULT_FILTERS_IDS_MAP.FILTERS_DRIVETRAIN_GROUP,
        title: i18n.t('defaultFilters.drivetrains.title'),
        gutter: [20, 20],
        order: 35,
        items: [
          {
            id: DEFAULT_FILTERS_IDS_MAP.FILTERS_DRIVETRAIN,
            key: DEFAULT_FILTERS_KEYS_MAP.DRIVETRAIN,
            path: 'drivetrain',
            span: 24,
            order: 1,
            type: ConstructorEnum.FilterCheckboxGroup,
            props: {},
          },
        ],
      },
      {
        id: DEFAULT_FILTERS_IDS_MAP.FILTERS_EXTERIOR_GROUP,
        title: i18n.t('defaultFilters.exteriors.title'),
        gutter: [20, 20],
        order: 40,
        items: [
          {
            id: DEFAULT_FILTERS_IDS_MAP.FILTERS_EXTERIOR,
            key: DEFAULT_FILTERS_KEYS_MAP.EXTERIOR,
            path: 'exterior',
            span: 24,
            order: 1,
            type: ConstructorEnum.FilterColorPicker,
            props: {},
          },
        ],
      },
    ],
  };
};

/**
 * Функция, которая возращает специально подготовленный `Grid` для фильтров на странице `Models`
 */
export const GET_DEFAULT_FILTERS_WITH_FEATURE_MODEL: () => FilterRootGridType = () => {
  return mergeGrid(GET_DEFAULT_FILTERS(), {
    items: [
      {
        id: DEFAULT_FILTERS_IDS_MAP.FILTERS_MODEL_GROUP,
        hidden: true,
        items: [
          {
            id: DEFAULT_FILTERS_IDS_MAP.FILTERS_MODEL,
            type: ConstructorEnum.FilterModelCheckboxGroup,
          },
        ],
      },
    ],
  });
};
